import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronDown, Search, Tent, X } from "lucide-react";
import type { TripLineString, TripSuggestion, TripSuggestionMember, TripSummary } from "@atlas/shared";
import { dismissTripSuggestion, fetchTripSuggestions, fetchTrips } from "../api/trips";
import { useTripGrouping } from "../hooks/useTripGrouping";
import { formatTripDates, formatTripDistance, plural } from "../lib/trip-format";
import { tripDayColor } from "./map/TripRouteMap";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";

// Trips on the Activities page (spec 35): the first trip suggestion as a card,
// and the user's trips as a compact panel above the activity list.

function suggestionCopy(s: TripSuggestion): string {
  if (s.kind === "split") {
    return s.activityIds.length === 2
      ? "Looks like one walk split in two. Group them?"
      : `Looks like one walk split into ${s.activityIds.length} recordings. Group them?`;
  }
  return `Looks like a ${s.days}-day trip. Group these ${s.activityIds.length} activities?`;
}

const SOURCE_LABELS: Record<string, string> = {
  strava: "Strava",
  komoot: "Komoot",
  apple_health: "Apple Health",
  health_connect: "Health Connect",
  garmin: "Garmin",
  gpx: "GPX",
};

const DAY = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short" });
const TIME = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });

/** "Sat 19 Jul, 09:02 to 15:40" (London time). */
function memberWhen(m: TripSuggestionMember): string {
  const start = new Date(m.startedAt);
  const end = new Date(m.endedAt);
  const sameDay = DAY.format(start) === DAY.format(end);
  return sameDay
    ? `${DAY.format(start)}, ${TIME.format(start)} to ${TIME.format(end)}`
    : `${DAY.format(start)}, ${TIME.format(start)} to ${DAY.format(end)}, ${TIME.format(end)}`;
}

/** The suggestion's activities: a sketch plus one row each, linked to the activity. */
function SuggestionMembers({ members }: { members: TripSuggestionMember[] }) {
  const lines = members.map((m) => m.geojson);
  return (
    <div
      className="r-wrap"
      style={{
        flexBasis: "100%",
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        paddingTop: 12,
        borderTop: "1px solid var(--border-subtle)",
        minWidth: 0,
      }}
    >
      <TripPreviewSvg lines={lines} size={112} />
      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2, flex: "1 1 260px", minWidth: 0 }}>
        {members.map((m, i) => (
          <li key={m.activityId} style={{ minWidth: 0 }}>
            <Link
              to="/activities/$id/review"
              params={{ id: m.activityId }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 8px",
                borderRadius: "var(--radius-md)",
                textDecoration: "none",
                minWidth: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-raised)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: tripDayColor(i), flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "var(--text-primary)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.name}
                </span>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>
                  {SOURCE_LABELS[m.sourceType] ?? m.sourceType} · {memberWhen(m)}
                </span>
              </span>
              <span className="tnum" style={{ fontSize: 12.5, color: "var(--text-secondary)", flexShrink: 0 }}>
                {m.distanceM != null ? formatTripDistance(m.distanceM) : ""}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** One suggestion: copy, figures, "Show activities", Not a trip / Make a trip. */
function SuggestionView({ s }: { s: TripSuggestion }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const grouping = useTripGrouping({
    onDone: (tripId) => void navigate({ to: "/trips/$tripId", params: { tripId } }),
  });
  const dismiss = useMutation({
    mutationFn: (firstActivityId: string) => dismissTripSuggestion(firstActivityId),
    // Prefix: the recent and the whole-history lists both refresh.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["trips", "suggestions"] }),
  });
  const [open, setOpen] = React.useState(false);
  const members = s.members ?? [];

  return (
    <div
      role="region"
      aria-label="Trip suggestion"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        padding: "14px 16px",
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm), var(--ring-top)",
        minWidth: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: "grid",
          placeItems: "center",
          background: "var(--info-soft)",
          color: "var(--status-info)",
          flexShrink: 0,
        }}
      >
        <Tent size={18} />
      </span>
      <div style={{ flex: "1 1 240px", minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{suggestionCopy(s)}</div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
          {s.suggestedName} · {formatTripDates(s.startedAt, s.endedAt)} · {formatTripDistance(s.distanceM)}
          {members.length > 0 && (
            <>
              {" · "}
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  padding: 0,
                  border: "none",
                  background: "none",
                  font: "inherit",
                  color: "var(--text-accent)",
                  cursor: "pointer",
                }}
              >
                {open ? "Hide" : "Show"} {plural(members.length, "activity", "activities")}
                <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform 120ms" }} />
              </button>
            </>
          )}
        </div>
        {grouping.error && (
          <div style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 4 }}>{grouping.error}</div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <Button variant="ghost" size="sm" disabled={dismiss.isPending} onClick={() => dismiss.mutate(s.firstActivityId)}>
          Not a trip
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Tent size={13} />}
          disabled={grouping.pending}
          onClick={() => grouping.createTrip(s.activityIds)}
        >
          {grouping.pending ? "Grouping…" : "Make a trip"}
        </Button>
      </div>
      {open && members.length > 0 && <SuggestionMembers members={members} />}
      {grouping.confirmElement}
    </div>
  );
}

export function TripSuggestionCard() {
  const query = useQuery({ queryKey: ["trips", "suggestions"], queryFn: () => fetchTripSuggestions(), staleTime: 60_000 });
  const [lookOlder, setLookOlder] = React.useState(false);
  const older = useQuery({
    queryKey: ["trips", "suggestions", "all"],
    queryFn: () => fetchTripSuggestions({ all: true }),
    enabled: lookOlder,
    staleTime: 60_000,
  });
  const s = query.data?.suggestions[0];
  if (!query.data) return null;
  const olderList = (older.data?.suggestions ?? []).filter((o) => o.firstActivityId !== s?.firstActivityId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      {/* key: a new suggestion (after "Not a trip" or "Make a trip") starts closed. */}
      {s && <SuggestionView key={s.firstActivityId} s={s} />}
      {!lookOlder ? (
        <button
          type="button"
          onClick={() => setLookOlder(true)}
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: 0,
            border: "none",
            background: "none",
            fontFamily: "var(--font-sans)",
            fontSize: 12.5,
            color: "var(--text-muted)",
            cursor: "pointer",
          }}
        >
          <Search size={13} /> Look for trips in older activities
        </button>
      ) : older.isLoading ? (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Looking through your older activities…</div>
      ) : older.isError ? (
        <div style={{ fontSize: 12.5, color: "var(--status-danger)" }}>
          Couldn't look through your older activities.{" "}
          <button
            type="button"
            onClick={() => void older.refetch()}
            style={{ padding: 0, border: "none", background: "none", font: "inherit", color: "var(--text-accent)", cursor: "pointer" }}
          >
            Try again
          </button>
        </div>
      ) : olderList.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>No other trips found in your older activities.</div>
      ) : (
        <>
          <div className="eyebrow" style={{ fontSize: 10.5, marginTop: 4 }}>
            {plural(olderList.length, s ? "more suggestion" : "suggestion")} from older activities
          </div>
          {olderList.map((o) => (
            <SuggestionView key={o.firstActivityId} s={o} />
          ))}
        </>
      )}
    </div>
  );
}

/** Tiny inline SVG of the trip's preview lines, coloured per member. */
export function TripPreviewSvg({ lines, size = 44 }: { lines: (TripLineString | null)[]; size?: number }) {
  // Nulls (no track) keep their slot so line i is always coloured tripDayColor(i).
  const all = lines.flatMap((l) => l?.coordinates ?? []);
  if (all.length < 2) {
    return (
      <span
        aria-hidden
        style={{ width: size, height: size, borderRadius: "var(--radius-md)", background: "var(--surface-raised)", flexShrink: 0 }}
      />
    );
  }
  // Equirectangular with a latitude correction, fitted into the box.
  const midLat = (Math.min(...all.map((c) => c[1])) + Math.max(...all.map((c) => c[1]))) / 2;
  const k = Math.cos((midLat * Math.PI) / 180);
  const xs = all.map((c) => c[0] * k);
  const ys = all.map((c) => c[1]);
  const minX = Math.min(...xs),
    maxX = Math.max(...xs),
    minY = Math.min(...ys),
    maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY) || 1;
  const pad = 4;
  const inner = size - pad * 2;
  const ox = pad + (inner - ((maxX - minX) / span) * inner) / 2;
  const oy = pad + (inner - ((maxY - minY) / span) * inner) / 2;
  const toPath = (l: TripLineString) =>
    l.coordinates
      .map((c, i) => {
        const x = ox + ((c[0] * k - minX) / span) * inner;
        const y = oy + ((maxY - c[1]) / span) * inner;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join("");
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ borderRadius: "var(--radius-md)", background: "var(--surface-raised)", flexShrink: 0 }}
    >
      {lines.map((l, i) => l && (
        <path key={i} d={toPath(l)} fill="none" stroke={tripDayColor(i)} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

function TripRow({ trip }: { trip: TripSummary }) {
  return (
    <Link
      to="/trips/$tripId"
      params={{ tripId: trip.id }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 10px",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        textDecoration: "none",
        minWidth: 0,
      }}
    >
      <TripPreviewSvg lines={trip.preview} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {trip.name}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
          {formatTripDates(trip.startedAt, trip.endedAt)}
          {" · "}
          {plural(trip.days, "day")}
          {trip.nights > 0 ? ` · ${plural(trip.nights, "night")}` : ""}
        </div>
      </div>
      <span className="tnum" style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", flexShrink: 0 }}>
        {formatTripDistance(trip.distanceM + (trip.filledM ?? 0))}
      </span>
    </Link>
  );
}

const COLLAPSED = 3;

export function TripsPanel() {
  const query = useQuery({ queryKey: ["trips"], queryFn: fetchTrips, staleTime: 30_000 });
  const [expanded, setExpanded] = React.useState(false);
  const trips = query.data?.trips ?? [];
  if (trips.length === 0) return null;
  const shown = expanded ? trips : trips.slice(0, COLLAPSED);

  return (
    <AtlasPanel
      title="Trips"
      action={
        <span className="eyebrow tnum" style={{ fontSize: 10 }}>
          {plural(trips.length, "trip")}
        </span>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {shown.map((t) => (
          <TripRow key={t.id} trip={t} />
        ))}
        {trips.length > COLLAPSED && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              alignSelf: "flex-start",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              color: "var(--text-accent)",
              padding: "6px 0 0",
            }}
          >
            {expanded ? "Show fewer" : `Show all ${trips.length}`}
          </button>
        )}
      </div>
    </AtlasPanel>
  );
}

/** Dismissable inline error line, used beside the select-mode toolbar. */
export function InlineError({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--status-danger)" }}>
      {message}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, display: "flex" }}
      >
        <X size={12} />
      </button>
    </span>
  );
}
